"use client";

import {
  Box,
  Button,
  Text,
  Flex,
  BoxProps,
  ButtonProps,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useState, useMemo, useCallback } from "react";
import { FaDownload, FaCheck, FaArrowDown } from "react-icons/fa";

enum DownloadStatus {
  IDLE = "idle",
  DOWNLOADING = "downloading",
  DOWNLOADED = "downloaded",
}

interface AnimatedDownloadButtonProps {
  downloadUrl?: string;
  onDownload?: () => Promise<void> | void;
  isDisabled?: boolean;
  iconSize?: number;
  showLabel?: boolean;
  buttonProps?: Partial<ButtonProps>;
  containerProps?: Partial<BoxProps>;
}

const animations = {
  pulse: keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `,
  bounce: keyframes`
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  `,
  shimmer: keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  `,
  downloadProgress: keyframes`
    0% { width: 0%; }
    100% { width: 100%; }
  `,
  float: keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  `,
  continuousPulse: keyframes`
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
  `,
  autoBounce: keyframes`
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-3px); }
    60% { transform: translateY(-1px); }
  `,
};

const styles = {
  button: {
    px: 2.5,
    py: 0.75,
    textStyle: "xs",
    minW: "auto",
    bg: "linear-gradient(135deg, #ffcb59 0%, #e6b24d 100%)",
    color: "white",
    position: "relative" as const,
    overflow: "hidden" as const,
    borderRadius: "xl",
    fontWeight: "bold",
    textTransform: "uppercase" as const,
    letterSpacing: "wide",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    _hover: {
      bg: "linear-gradient(135deg, #ffc957 0%, #c2943d 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 25px rgba(255, 203, 89, 0.4)",
      _before: {
        left: "100%",
      },
    },
    _active: {
      transform: "translateY(0px) scale(0.98)",
    },
    _before: {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
      transition: "left 0.5s",
    },
  },
  container: {
    position: "relative" as const,
    display: "inline-block" as const,
  },
  progressBar: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    height: 0.75,
    bg: "rgba(255,255,255,0.3)",
    borderRadius: "0 0 xl xl",
    zIndex: 2,
  },
  pulseBurst: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    w: "0",
    h: "0",
    bg: "rgba(255,255,255,0.3)",
    borderRadius: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 2,
  },
  shimmerOverlay: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    backgroundSize: "200% 100%",
    transition: "opacity 0.3s",
    _hover: { opacity: 1 },
  },
  hoverIndicator: {
    position: "absolute" as const,
    top: -5,
    left: "50%",
    transform: "translateX(-50%)",
    w: 0.25,
    h: 0.25,
    bg: "primary.100",
    borderRadius: "50%",
    opacity: 0.7,
  },
} as const;

const statusConfig = {
  [DownloadStatus.IDLE]: {
    label: "Download",
    icon: FaDownload,
    iconAnimation: undefined,
    buttonAnimation: `${animations.continuousPulse} 2s ease-in-out infinite`,
  },
  [DownloadStatus.DOWNLOADING]: {
    label: "Downloading...",
    icon: FaArrowDown,
    iconAnimation: `${animations.bounce} 0.8s infinite`,
    buttonAnimation: undefined,
  },
  [DownloadStatus.DOWNLOADED]: {
    label: "Downloaded!",
    icon: FaCheck,
    iconAnimation: undefined,
    buttonAnimation: `${animations.pulse} 0.4s ease-in-out`,
  },
};

const getStatusConfig = (status: DownloadStatus) => statusConfig[status];

const getIconAnimation = (
  status: DownloadStatus,
  isHovered: boolean,
): string | undefined => {
  const config = getStatusConfig(status);
  if (config.iconAnimation) return config.iconAnimation;
  if (status === DownloadStatus.IDLE) {
    return isHovered
      ? `${animations.float} 1.5s infinite`
      : `${animations.autoBounce} 1.5s ease-in-out infinite`;
  }
  return undefined;
};

export const AnimatedDownloadButton = ({
  downloadUrl,
  onDownload,
  isDisabled = false,
  iconSize = 2,
  showLabel = false,
  buttonProps: customButtonProps = {},
  containerProps: customContainerProps = {},
}: AnimatedDownloadButtonProps) => {
  const [status, setStatus] = useState<DownloadStatus>(DownloadStatus.IDLE);
  const [isHovered, setIsHovered] = useState(false);

  const handleDownload = useCallback(async () => {
    if (isDisabled || status !== DownloadStatus.IDLE || !downloadUrl) return;

    setStatus(DownloadStatus.DOWNLOADING);

    try {
      if (onDownload) {
        await onDownload();
      } else {
        window.open(downloadUrl, "_blank");
      }

      setTimeout(() => {
        setStatus(DownloadStatus.DOWNLOADED);

        setTimeout(() => {
          setStatus(DownloadStatus.IDLE);
        }, 3000);
      }, 2000);
    } catch (error) {
      setStatus(DownloadStatus.IDLE);
      console.error("Download failed:", error);
    }
  }, [isDisabled, status, downloadUrl, onDownload]);

  const config = useMemo(() => getStatusConfig(status), [status]);
  const iconAnimation = useMemo(
    () => getIconAnimation(status, isHovered),
    [status, isHovered],
  );

  const isButtonDisabled = isDisabled || status === DownloadStatus.DOWNLOADING;

  return (
    <Box {...styles.container} {...customContainerProps}>
      <Button
        {...styles.button}
        {...customButtonProps}
        onClick={handleDownload}
        disabled={isButtonDisabled}
        animation={config.buttonAnimation}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          {...styles.shimmerOverlay}
          animation={`${animations.shimmer} 2s infinite`}
        />

        <Flex align="center" gap={2} position="relative" zIndex={1}>
          <Box animation={iconAnimation} transition="all 0.3s">
            <config.icon size={iconSize} />
          </Box>
          {showLabel && (
            <Text textStyle={styles.button.textStyle} fontWeight="bold">
              {config.label}
            </Text>
          )}
        </Flex>

        {status === DownloadStatus.DOWNLOADING && (
          <Box
            {...styles.progressBar}
            animation={`${animations.downloadProgress} 2s ease-out`}
          />
        )}

        {status === DownloadStatus.DOWNLOADED && (
          <Box
            {...styles.pulseBurst}
            animation={`${animations.pulse} 0.6s ease-out`}
          />
        )}
      </Button>

      {isHovered && (
        <Box
          {...styles.hoverIndicator}
          animation={`${animations.float} 2s infinite`}
        />
      )}
    </Box>
  );
};
